import { HHAlert, HHBox, HHStack } from '@hinge-health/react-component-library';
import { Skeleton, Tab, Tabs, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { useParams } from 'react-router-dom';
import { AssessmentsTab } from '../components/assessments-tab';
import { BackLink } from '../components/back-link';
import { DiagnosticsTab } from '../components/diagnostics-tab';
import { IngestionHeader } from '../components/ingestion-header';
import { OverviewTab } from '../components/overview-tab';
import { ParsingTab } from '../components/parsing-tab';
import { PipelineTimeline } from '../components/pipeline-timeline';
import { RecordsTab } from '../components/records-tab';
import { SurfaceCard } from '../components/surface-card';
import { ValidationTab } from '../components/validation-tab';
import { PARTNER_DATA_GATEWAY_ROUTES } from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { usePermissions } from '../context/permissions-context';
import {
  IngestionDetailTab,
  INGESTION_DETAIL_TABS,
  useDetailTab,
} from '../hooks/use-detail-tab';
import { useIngestionDetail } from '../hooks/use-ingestion-detail';

export const INGESTION_DETAIL_PAGE_TEST_ID =
  'partner-data-gateway-ingestion-detail-page';
export const INGESTION_DETAIL_NO_ACCESS_TEST_ID =
  'partner-data-gateway-ingestion-detail-no-access';
export const INGESTION_DETAIL_BACK_LINK_TEST_ID = 'ingestion-detail-back-link';
export const INGESTION_DETAIL_BACK_LINK_LABEL = 'Back to ingestions';
export const INGESTION_DETAIL_LOADING_TEST_ID = 'ingestion-detail-loading';
export const INGESTION_DETAIL_ERROR_TEST_ID =
  'partner-data-gateway-ingestion-detail-error';
export const INGESTION_DETAIL_STALE_WARNING_TEST_ID =
  'partner-data-gateway-ingestion-detail-stale-warning';
export const INGESTION_DETAIL_TABS_TEST_ID = 'ingestion-detail-tabs';

/** Accessible name for the tab strip (WCAG 4.1.2). */
export const INGESTION_DETAIL_TABS_LABEL = 'Ingestion detail';

/** Ids that tie each tab to its panel for assistive tech. */
export const tabId = (tab: string): string => `ingestion-detail-tab-${tab}`;
export const tabPanelId = (tab: string): string =>
  `ingestion-detail-tabpanel-${tab}`;

const TAB_LABELS: Record<IngestionDetailTab, string> = {
  overview: 'Overview',
  parsing: 'Parsing',
  validation: 'Validation',
  assessments: 'Assessments',
  records: 'Records',
  diagnostics: 'Diagnostics',
};

/** Tabs with a real panel today; the rest render a "coming soon" note. */
const IMPLEMENTED_TABS: IngestionDetailTab[] = [
  'overview',
  'parsing',
  'records',
  'validation',
  'assessments',
  'diagnostics',
];

const FUTURE_TAB_TEST_ID_PREFIX = 'ingestion-detail-future-tab';

const FutureTabPlaceholder = ({
  tab,
}: {
  tab: IngestionDetailTab;
}): JSX.Element => (
  <HHBox data-testid={`${FUTURE_TAB_TEST_ID_PREFIX}-${tab}`}>
    <HHAlert hhVariant="outlined" severity="info">
      {`${TAB_LABELS[tab]} is coming soon.`}
    </HHAlert>
  </HHBox>
);

/** Card-shaped placeholder that mirrors the header + timeline hero. */
const LoadingSkeleton = (): JSX.Element => (
  <SurfaceCard padding={6} data-testid={INGESTION_DETAIL_LOADING_TEST_ID}>
    <HHStack gap={2}>
      <Skeleton
        variant="rectangular"
        width={96}
        height={24}
        sx={{ borderRadius: 1 }}
      />
      <Skeleton variant="text" width="60%" height={36} />
      <Skeleton variant="text" width="40%" />
      <Skeleton
        variant="rectangular"
        height={72}
        sx={{ marginTop: 4, borderRadius: 1 }}
      />
    </HHStack>
  </SurfaceCard>
);

const tabsSx = (theme: Theme): SystemStyleObject<Theme> => ({
  paddingX: 4,
  borderBottom: `1px solid ${theme.palette.divider}`,
});

export const IngestionDetailPage = (): JSX.Element => {
  const { eventId } = useParams<{ eventId: string }>();
  const basePath = useBasePath();
  const { canViewIngestions, canViewPhi } = usePermissions();
  const { activeTab, setActiveTab } = useDetailTab();
  const { ingestion, isLoading, hasError, isControlling, controlIngestion } =
    useIngestionDetail(eventId ?? '', { skip: !eventId });

  // Records shows raw parsed rows (PHI), so it is only offered to users the
  // BFF's `GqlPhiGuard` would actually let through. A deep link to
  // `?tab=records` still resolves — `<RecordsTab>` renders its own
  // access-denied message — but then no tab in the strip is selected, so the
  // strip's value has to be `false` rather than a missing tab.
  const visibleTabs = INGESTION_DETAIL_TABS.filter(
    tab => tab !== 'records' || canViewPhi,
  );
  const tabsValue = visibleTabs.includes(activeTab) ? activeTab : false;

  // Access control lives on the page, not just the route, so the module stays
  // safe wherever the host mounts it.
  if (!canViewIngestions) {
    return (
      <HHAlert hhVariant="outlined" severity="warning">
        <span data-testid={INGESTION_DETAIL_NO_ACCESS_TEST_ID}>
          You do not have access to view partner data ingestions.
        </span>
      </HHAlert>
    );
  }

  return (
    // The parent owns horizontal padding so the back link, hero card and
    // tabs share one left edge with the ingestions list page.
    <HHStack
      flex={1}
      direction="column"
      gap={4}
      paddingX={6}
      paddingY={6}
      data-testid={INGESTION_DETAIL_PAGE_TEST_ID}
    >
      <BackLink
        // Route is derived from the mount point, never hardcoded (README rule 2).
        to={`${basePath}/${PARTNER_DATA_GATEWAY_ROUTES.ingestions}`}
        label={INGESTION_DETAIL_BACK_LINK_LABEL}
        testId={INGESTION_DETAIL_BACK_LINK_TEST_ID}
      />

      {isLoading && !ingestion && <LoadingSkeleton />}

      {!isLoading && !ingestion && (hasError || !eventId) && (
        <HHBox data-testid={INGESTION_DETAIL_ERROR_TEST_ID}>
          <HHAlert hhVariant="outlined" severity="error">
            Unable to load this ingestion. Please try again.
          </HHAlert>
        </HHBox>
      )}

      {/* A poll failed but the last good snapshot is still on screen, so warn
          instead of replacing the page with an error. */}
      {hasError && ingestion && (
        <HHBox data-testid={INGESTION_DETAIL_STALE_WARNING_TEST_ID}>
          <HHAlert hhVariant="outlined" severity="warning">
            Live updates paused — showing the last successful snapshot.
            Retrying…
          </HHAlert>
        </HHBox>
      )}

      {ingestion && (
        <>
          <SurfaceCard padding={6}>
            <IngestionHeader
              ingestion={ingestion}
              isControlling={isControlling}
              controlIngestion={controlIngestion}
            />
            <HHBox marginTop={5}>
              <PipelineTimeline
                stageHistory={ingestion.stageHistory}
                liveProgressSource={ingestion.liveProgressSource}
              />
            </HHBox>
          </SurfaceCard>

          <SurfaceCard sx={{ overflow: 'hidden' }}>
            <Tabs
              value={tabsValue}
              onChange={(_event, value: IngestionDetailTab): void =>
                setActiveTab(value)
              }
              aria-label={INGESTION_DETAIL_TABS_LABEL}
              data-testid={INGESTION_DETAIL_TABS_TEST_ID}
              sx={tabsSx}
            >
              {visibleTabs.map(tab => (
                <Tab
                  key={tab}
                  value={tab}
                  label={TAB_LABELS[tab]}
                  id={tabId(tab)}
                  // Only the active tab's panel is mounted, so pointing an
                  // inactive tab at it would be a dangling reference.
                  aria-controls={
                    tab === activeTab ? tabPanelId(tab) : undefined
                  }
                  data-testid={tabId(tab)}
                />
              ))}
            </Tabs>

            <HHBox
              padding={5}
              role="tabpanel"
              id={tabPanelId(activeTab)}
              /*
               * Deep-linking to a PHI-gated tab without access leaves the tab
               * itself unrendered while its panel still shows an access-denied
               * message, so the label reference would dangle.
               */
              aria-labelledby={
                visibleTabs.includes(activeTab) ? tabId(activeTab) : undefined
              }
            >
              {activeTab === 'overview' && (
                <OverviewTab ingestion={ingestion} />
              )}
              {activeTab === 'parsing' && (
                <ParsingTab
                  eventId={ingestion.eventId}
                  parentStatus={ingestion.status}
                />
              )}
              {activeTab === 'records' && (
                <RecordsTab eventId={ingestion.eventId} />
              )}
              {activeTab === 'validation' && (
                <ValidationTab eventId={ingestion.eventId} />
              )}
              {activeTab === 'assessments' && (
                <AssessmentsTab eventId={ingestion.eventId} />
              )}
              {activeTab === 'diagnostics' && (
                <DiagnosticsTab ingestion={ingestion} />
              )}
              {!IMPLEMENTED_TABS.includes(activeTab) && (
                <FutureTabPlaceholder tab={activeTab} />
              )}
            </HHBox>
          </SurfaceCard>
        </>
      )}
    </HHStack>
  );
};
